import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Series3CollarDetails from './Series3CollarDetails';
import { useDispatch, useSelector } from 'react-redux';
import { cartActions as storeCartActions } from '../../../reducers/storeCart';
import { useHistory } from 'react-router-dom';
import AppPaths from '../../../AppPaths';
import * as types from '../../../types';
import useSubscriptionProducts from '../../../hooks/useSubscriptionProducts';
import ReviewsCarousel from '../../../components/ReviewsCarousel';
import Footer from '../../../components/Footer';
import { PromotionBanner } from '@barkinglabs/promotions-react';
import { PromotionConfigurationKind } from '@barkinglabs/promotions-types';
import Loading from '../../../components/Loading';
import { generateID, getLocalizedPricing } from '../../../lib/util';
import { applyCouponToCart } from '../../../lib/promoCode';
import { useCartMode } from '../../../lib/cartModes';
import setCouponCookie from '../../../lib/setCouponCookie';
import setPartnerCookie from '../../../lib/setPartnerCookie';
import { useLocale } from '../../../contexts/LocaleContext';

export const VipRedeemSeries3Collar = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const products = useSelector((state: types.AppState) => state.config.products);
  const siteConfig = useSelector((state: types.AppState) => state.config.siteConfig);
  const gwpFreeTrialMonths = siteConfig.gwpFreeTrialMonths;

  const { currency, country } = useLocale();

  const { cartSelector } = useCartMode();
  const cart = useSelector(cartSelector);

  const productId = types.series3PlusCollarId;

  const product = useMemo(() => {
    return products.find((p) => p.id === productId) as types.ICollarKitProduct;
  }, [products, productId]);

  const subscriptionProducts = useSubscriptionProducts(productId);
  const filteredSubscriptionProducts = useMemo(
    () =>
      subscriptionProducts.filter(
        (subscriptionProduct) => subscriptionProduct.renewalMonths === siteConfig.gwpRenewalMonths,
      ),
    [subscriptionProducts, siteConfig.gwpRenewalMonths],
  );

  const [validatingCouponCode, setValidatingCouponCode] = useState(true);
  const [isValidCouponCode, setIsValidCouponCode] = useState(false);

  useEffect(() => {
    const validateCoupon = async () => {
      const queryParameters = new URLSearchParams(window.location.search);
      const urlPartnerCode = queryParameters.get('pc');
      if (!urlPartnerCode) {
        setValidatingCouponCode(false);
        return;
      }

      if (cart.couponCode === urlPartnerCode) {
        setIsValidCouponCode(true);
        setValidatingCouponCode(false);
        return;
      }

      try {
        const cartItemId = generateID();
        const mockCart: types.SubscriptionCart = {
          cartItems: {
            [cartItemId]: {
              forModuleId: productId,
              cartItemId,
              lineItem: {
                lineItemId: generateID(),
                sku: filteredSubscriptionProducts[0].sku,
              },
              quantity: 1,
            },
          },
          currency: currency,
        };

        const result = await applyCouponToCart(mockCart, urlPartnerCode);
        if (result.kind === 'success' && result.value.discount.type === 'free_trial') {
          setCouponCookie(urlPartnerCode);
          setPartnerCookie(true);
          dispatch(storeCartActions.addCoupon(urlPartnerCode));
          setIsValidCouponCode(true);
        }
      } catch (error) {
        console.error('Error validating coupon:', error);
      } finally {
        setValidatingCouponCode(false);
      }
    };

    validateCoupon();
  }, [cart.couponCode, dispatch, filteredSubscriptionProducts, productId, currency, country]);

  if (validatingCouponCode) {
    return <Loading />;
  }

  return (
    <>
      {
        <PromotionBanner
          className={'promotionBanner'}
          promotion={{
            configuration: {
              kind: PromotionConfigurationKind.SIMPLE,
              title: isValidCouponCode
                ? t('exclusive_offer_months_free_applied_at_checkout', { months: gwpFreeTrialMonths })
                : t('exclusive_offer_add_promo_code_at_checkout_for_months_free', { months: gwpFreeTrialMonths }),
              extraCss: `.fi-promotion__promoContainer { position: sticky; top: 0; z-index: 9999; width: 100%; background-color: #FFFF01; }`,
              countdown: 'none',
              imageUrl: undefined,
              backgroundImageUrl: undefined,
              subtitle: undefined,
              couponCode: undefined,
            },
          }}
          promotionEnd={new Date()}
        />
      }
      <Series3CollarDetails
        addBundleCartItem={(variantSku, subscriptionSku, addonSkus) => {
          dispatch(
            storeCartActions.addBundleCartItem({
              sku: variantSku,
              subscriptionSku: subscriptionSku,
              addonSkus: addonSkus,
              currency,
            }),
          );

          history.push(AppPaths.Bag);
        }}
        priceInCents={getLocalizedPricing(product.pricingInCents, currency)}
        productName={product.name}
        variants={product.variants}
        availableSubscriptions={filteredSubscriptionProducts}
        redemptionType="gwp_free_trial"
        hasValidRedemptionCode={isValidCouponCode}
        preselectedSubscriptionSku={filteredSubscriptionProducts[0].sku}
      />
      <ReviewsCarousel />
      <Footer />
    </>
  );
};
